// THE OPEN STEP — ready → open, the ENLIVENING, and the second of the surface's
// two points of no return. Opening starts the binding vote, so it is a real
// `<form>` behind a Cancel-first confirm, and the numbers it sends are the
// Administrator's DECLARED count of who is in the room.
//
// ── THE REGISTER IS SHOWN, NOT SPENT ───────────────────────────────────────
// The attendance register's per-House counts are shown BESIDE the fields as a
// bordered, read-only aside — never poured INTO them. Quorum is a declared act,
// not a derived one (registrar 2026-08-16, restated by Steve 2026-08-29): the
// operator looks at the room, looks at what the register says, and TYPES a figure.
// The fields start empty ({@link initialQuorumInputs}) and this component never
// writes a register count into one. A pre-filled field would be a computed quorum
// wearing a declaration's clothes — exactly the derivation the ruling struck.
//
// ── THREE FIGURES, NO TOTAL ────────────────────────────────────────────────
// SO 12.1 is a per-House test, so there is one field per House and nothing that
// sums them. The body sent is `{ clergy, laity, bishop }` and nothing else.
//
// ── ONE WAY BACK, AND IT ASKS FIRST ────────────────────────────────────────
// This step's "Back to setup" is the only back on the screen: SetupPage passes
// `hideBack` so the frame's own back is not stacked above it (cca-agfn). Once any
// figure is typed, pressing it asks before the figures are thrown away — the
// builder's guard (`BallotBuilder`'s `handleBack`), mirrored. With nothing typed
// there is nothing to lose, so it goes straight back. The confirm takes the back
// button's slot, so its Cancel hands focus back to "Back to setup" when that button
// returns, rather than letting it fall to <body> (WCAG 2.2 SC 2.4.3).

import { useEffect, useRef, useState, type JSX } from 'react';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import { ConfirmBlock } from './ConfirmBlock';
import { refusalSentence } from './copy';
import {
  initialQuorumInputs,
  isQuorumComplete,
  parseQuorum,
  type QuorumField,
  type QuorumInputs,
} from './state';
import type {
  DraftBallot,
  QuorumDeclaration,
  RegisterHouse,
  RegisterHouseCount,
} from '../api/client';

/** The Houses in the order every surface on this platform states them, with the
 *  field key each maps to. The Bishop is counted on his own — never a third
 *  House, but a figure of his own (registrar 2026-08-14). */
const HOUSE_FIELDS: { house: RegisterHouse; key: QuorumField; label: string }[] = [
  { house: 'clergy', key: 'clergy', label: 'Clergy' },
  { house: 'laity', key: 'laity', label: 'Laity' },
  { house: 'bishop', key: 'bishop', label: 'Bishop' },
];

interface Props {
  ballot: DraftBallot;
  /** The register's per-House counts, as at the hub's last read — REFERENCE only,
   *  never a source for the fields. */
  reference: RegisterHouseCount[];
  busy: boolean;
  /** The wire code of the last open refusal, or `null`. */
  error: string | null;
  onOpen: (quorum: QuorumDeclaration) => void;
  onCancel: () => void;
}

export function OpenStep({ ballot, reference, busy, error, onOpen, onCancel }: Props): JSX.Element {
  const [inputs, setInputs] = useState<QuorumInputs>(initialQuorumInputs);
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Move focus to the heading on entry — the same FocusHeading pattern SetupPage
  // uses on its two result views (a `tabIndex={-1}` heading focused on mount). The
  // Open step is the binding act, so the keyboard lands on its title rather than
  // dropping to <body>.
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  // CANCEL GIVES FOCUS BACK TO THE CONTROL IT REPLACED. Pressing the leave confirm's
  // Cancel unmounts the button that held focus, and "Back to setup" mounts in its
  // place. A flag the Cancel sets, consumed once `leaving` is false and the button is
  // back — not `autoFocus`, which would also fire on the step's first mount and take
  // focus off the heading above.
  const backButton = useRef<HTMLButtonElement>(null);
  const focusBackOnReturn = useRef(false);
  useEffect(() => {
    if (!leaving && focusBackOnReturn.current) {
      focusBackOnReturn.current = false;
      backButton.current?.focus();
    }
  }, [leaving]);

  const byHouse = new Map(reference.map((count) => [count.house, count] as const));
  const complete = isQuorumComplete(inputs);
  // Any figure typed, in any House, is something a stray press would lose.
  const dirty = HOUSE_FIELDS.some((field) => inputs[field.key].trim() !== '');

  const setField = (key: QuorumField, value: string): void => {
    // A keystroke reopens the form from a confirm — the operator has changed the
    // number, so the two-tap starts again rather than confirming a stale figure.
    setConfirming(false);
    setInputs((current) => ({ ...current, [key]: value }));
  };

  const confirmOpen = (): void => {
    const parsed = parseQuorum(inputs);
    // `complete` already gates the button, so this is belt-and-braces; a malformed
    // set never reaches here, and if it did nothing is sent.
    if (parsed.ok) onOpen(parsed.declaration);
  };

  const handleBack = (): void => {
    if (dirty) setLeaving(true);
    else onCancel();
  };

  return (
    <section className="setup-open" aria-labelledby="setup-open-heading">
      {/* The leave confirm sits in the back button's slot and keeps its space before
          the heading (`.setup-open-leave`) — a wrapper, so the shared ConfirmBlock is
          not widened for one caller. */}
      {leaving ? (
        <div className="setup-open-leave">
          <ConfirmBlock
            prompt="Leave and lose the figures you have typed?"
            confirmLabel="Yes, leave without opening"
            busy={busy}
            onConfirm={onCancel}
            onCancel={() => {
              focusBackOnReturn.current = true;
              setLeaving(false);
            }}
          />
        </div>
      ) : (
        <button
          ref={backButton}
          type="button"
          className="btn btn-ghost setup-back"
          onClick={handleBack}
          disabled={busy}
        >
          Back to setup
        </button>
      )}

      <h1 className="ballot-title" id="setup-open-heading" tabIndex={-1} ref={heading}>
        Open “{ballot.title}”
      </h1>
      <p className="setup-lede">
        Opening starts the binding vote. Members can mark their papers the moment it is open, and it
        cannot be closed back to a draft — you would abandon it and build again. Declare who is
        present in each House, then open it.
      </p>

      {/* THE REGISTER, FOR REFERENCE — bordered, read-only, and deliberately not a
          set of inputs. It says what the register holds; the operator declares
          what they see. */}
      <aside className="setup-reference" aria-label="The attendance register, for reference">
        <p className="setup-reference-lead">
          For reference — the attendance register as at now. Quorum is{' '}
          <strong>yours to declare</strong>; these figures are not entered for you.
        </p>
        <table className="table setup-reference-table">
          <thead>
            <tr>
              <th scope="col">
                <span className="ballot-sr">What is recorded</span>
              </th>
              {HOUSE_FIELDS.map((field) => (
                <th scope="col" className="num" key={field.house}>
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Present</th>
              {HOUSE_FIELDS.map((field) => (
                <td className="num" key={field.house}>
                  {byHouse.get(field.house)?.present ?? '—'}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">On the roll</th>
              {HOUSE_FIELDS.map((field) => (
                <td className="num" key={field.house}>
                  {byHouse.get(field.house)?.onRoll ?? '—'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </aside>

      {error !== null ? (
        <p className="alert" role="alert">
          {refusalSentence(error)}
        </p>
      ) : null}

      {/* A REAL FORM. `onSubmit` is prevented — the submit is the confirm, which is
          a deliberate second tap, not the first Enter in a field. */}
      <form
        className="setup-open-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !confirming) setConfirming(true);
        }}
      >
        <fieldset className="setup-fieldset">
          <legend className="setup-legend">How many are present, per House?</legend>
          <div className="setup-quorum-fields">
            {HOUSE_FIELDS.map((field) => {
              const inputId = `quorum-${field.key}`;
              const hintId = `${inputId}-hint`;
              const isBishop = field.key === 'bishop';
              // The Bishop caps at 1; the others have no upper bound. Spread rather
              // than pass `max={undefined}` so nothing hands an optional attribute
              // an explicit undefined.
              const cap = isBishop ? { max: 1 } : {};
              return (
                <div className="setup-field" key={field.key}>
                  <label htmlFor={inputId}>{field.label}</label>
                  <input
                    id={inputId}
                    aria-describedby={hintId}
                    className="setup-input setup-input-num"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    {...cap}
                    value={inputs[field.key]}
                    disabled={busy}
                    onChange={(event) => setField(field.key, event.target.value)}
                  />
                  <span className="setup-field-hint" id={hintId}>
                    {isBishop ? '0 or 1' : 'a whole number, 0 or more'}
                  </span>
                </div>
              );
            })}
          </div>
        </fieldset>

        {confirming ? (
          <ConfirmBlock
            prompt={`Open “${ballot.title}” now? This starts the binding vote and cannot be undone.`}
            confirmLabel="Yes, open the ballot"
            busy={busy}
            onConfirm={confirmOpen}
            onCancel={() => setConfirming(false)}
          />
        ) : (
          <button
            type="submit"
            className="btn btn-primary setup-primary"
            disabled={!complete || busy}
          >
            Open the ballot
          </button>
        )}
      </form>
    </section>
  );
}
